import { Box, Card, Divider, InputAdornment, List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import type { InvoiceBilledVisitRow } from "./InvoiceBilledVisitsCard";

export type InvoicePerJobLineGroup = {
  jobId: number;
  jobLabel: string;
  rows: InvoiceBilledVisitRow[];
};

type Props = {
  groups: InvoicePerJobLineGroup[];
  lineAmountByEventUid: Record<string, string>;
  onLineAmountChange: (eventUid: string, raw: string) => void;
  isLoading?: boolean;
  showValidation?: boolean;
  afterLineItems?: ReactNode;
  totalsSection?: ReactNode;
};

function formatMoney(n: number) {
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function InvoicePerJobLineItemsCard({
  groups,
  lineAmountByEventUid,
  onLineAmountChange,
  isLoading,
  showValidation = false,
  afterLineItems,
  totalsSection,
}: Props) {
  if (isLoading) {
    return (
      <Card elevation={0} sx={invoiceEditorSectionCardSx}>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Loading job pricing…
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box sx={{ p: 2, borderBottom: invoiceEditorInnerBorder }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
          Service Items
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 0.5 }}>
          Each visit has its own amount, usually from the job quote or price override.
        </Typography>
      </Box>

      <Stack divider={<Divider flexItem />} spacing={0}>
        {groups.map((group) => (
          <Box key={group.jobId}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 2,
                bgcolor: "grey.50",
                borderBottom: invoiceEditorInnerBorder,
              }}
            >
              <Typography sx={{ fontWeight: 700, flex: "1 1 200px", fontSize: "0.9375rem" }}>{group.jobLabel}</Typography>
            </Box>
            <List disablePadding sx={{ bgcolor: "grey.100" }}>
              {group.rows.map((event, idx) => {
                const value = lineAmountByEventUid[event.uid] ?? "";
                const parsed = Number.parseFloat(String(value).replace(/,/g, ""));
                const hasValidAmount = Number.isFinite(parsed) && parsed > 0;
                const hint = hasValidAmount ? formatMoney(parsed) : "Enter amount";
                const amountError = showValidation && !hasValidAmount;

                return (
                  <ListItem
                    key={event.uid}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderBottom: invoiceEditorInnerBorder,
                      "&:last-of-type": { borderBottom: "none" },
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Visit {idx + 1}
                          {event.title ? ` — ${event.title}` : ""}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" component="span">
                          {event.subtitle}
                        </Typography>
                      }
                      sx={{ flex: "1 1 200px" }}
                    />
                    <TextField
                      size="small"
                      label="Service amount"
                      type="number"
                      inputProps={{ min: 0, step: "0.01" }}
                      value={value}
                      onChange={(e) => onLineAmountChange(event.uid, e.target.value)}
                      onWheel={(e) => {
                        // Prevent accidental wheel changes while scrolling.
                        (e.target as HTMLInputElement).blur();
                      }}
                      sx={{
                        width: 160,
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                      }}
                      required
                      error={amountError}
                      helperText={amountError ? "Service amount is required and must be greater than 0." : hint}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Stack>
      {afterLineItems}
      {totalsSection}
    </Card>
  );
}
